import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { UserProfile } from '../types';
import { apiService } from '../services/api';
import { authService } from '../services/auth';
import { ProfileHeader } from '../components/ProfileHeader';
import { PollCard } from '../components/PollCard';
import { CreatePollForm } from '../components/CreatePollForm';
import './UserProfilePage.css';

interface UserProfilePageProps {
  userId?: string;
  showCreateForm?: boolean;
  onShowCreateFormChange?: (show: boolean) => void;
}

export const UserProfilePage: React.FC<UserProfilePageProps> = ({
  userId: propUserId,
  showCreateForm = false,
  onShowCreateFormChange,
}) => {
  const { userId: paramUserId } = useParams<{ userId: string }>();
  const userId = propUserId || paramUserId || '';
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const userInfo = authService.getUserInfo();
  const isOwner = userInfo?.sub === userId;

  useEffect(() => {
    const fetchProfile = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const userProfile = await apiService.getUserProfile(userId);
        setProfile(userProfile);
      } catch (err: any) {
        setError(err.message || 'Failed to load user profile');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, [userId]);

  const handlePollCreated = () => {
    if (onShowCreateFormChange) {
      onShowCreateFormChange(false);
    }
    // Refresh the profile to show the new poll if it's the current user
    const fetchProfile = async () => {
      try {
        const userProfile = await apiService.getUserProfile(userId);
        setProfile(userProfile);
      } catch (err) {
        // Ignore errors on refresh
      }
    };
    fetchProfile();
  };

  const handleDelete = (deletedPollId: string) => {
    // Remove the deleted poll from the profile's polls list
    if (profile) {
      setProfile({
        ...profile,
        pollsCreated: profile.pollsCreated.filter(p => p.pollId !== deletedPollId),
        totalPollsCreated: profile.totalPollsCreated - 1,
      });
    }
  };

  if (isLoading) {
    return (
      <div className="user-profile-page">
        <div className="user-profile-container">
          <div className="loading">Loading profile...</div>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="user-profile-page">
        <div className="user-profile-container">
          <div className="error">{error || 'Failed to load profile'}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="user-profile-page">
      <div className="user-profile-container">

        <ProfileHeader
          profileImageUrl={profile.profileImageUrl}
          userName={profile.userName}
          totalPollsCreated={profile.totalPollsCreated}
          linkTo={isOwner ? "/activity?tab=created" : undefined}
        />

        <div className="user-polls-section">
          <h3 className="section-title">Polls Created</h3>
          {profile.pollsCreated.length === 0 ? (
            <div className="no-polls">This user hasn't created any polls yet.</div>
          ) : (
            <div className="polls-list">
              {profile.pollsCreated.map((poll) => (
                <PollCard key={poll.pollId} poll={poll} onDelete={handleDelete} />
              ))}
            </div>
          )}
        </div>
      </div>

      {onShowCreateFormChange && (
        <CreatePollForm
          open={showCreateForm}
          onOpenChange={onShowCreateFormChange}
          onPollCreated={handlePollCreated}
        />
      )}
    </div>
  );
};
